"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Phone, ShieldAlert } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function FinalCTA() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const featureItems = [{
    title: "Direct local technician dispatch",
    detail: "Zero third-party call centers"
  }, {
    title: "Immediate structural stabilization",
    detail: "Stops mold colonization within 24-48 hours"
  }, {
    title: "Direct insurance coordination",
    detail: "Documented moisture logs sent to your adjuster"
  }];
  return <section id="final-cta" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden border-t border-border">
      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-24 lg:px-8 lg:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative rounded-2xl border border-border/80 bg-secondary text-secondary-foreground p-8 md:p-12 lg:p-16 shadow-2xl overflow-hidden">
          {/* Subtle technical background grid overlay */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-10 bg-[linear-gradient(to_right,hsl(var(--color-secondary-foreground))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--color-secondary-foreground))_1px,transparent_1px)] bg-[size:32px_32px]" />

          <div className="relative z-10 flex flex-col gap-8 lg:gap-10">
            {/* Technical status badge */}
            <div className="inline-flex items-center gap-2.5 self-start rounded-full border border-primary/40 bg-primary/20 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              <span>24/7 Rapid Response • Maryland, DC & Northern Virginia</span>
            </div>

            {/* Primary Headline & Paragraph */}
            <div className="max-w-3xl space-y-4">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-secondary-foreground font-default">
                Stop secondary damage before it spreads.
              </h2>
              <p className="text-base sm:text-lg text-secondary-foreground/80 leading-relaxed font-default">
                When water, fire, smoke, or hazardous contaminants hit your property, every passing hour allows moisture migration, soot penetration, and microbial growth to double. Our field units deploy directly with commercial extraction pumps, HEPA filtration, and structural drying equipment.
              </p>
            </div>

            {/* Critical Interactive Action Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="h-14 px-8 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-lg font-bold shadow-lg transition-[background-color,transform] duration-160 hover:-translate-y-0.5">
                <a href="tel:8554356631" className="inline-flex items-center justify-center gap-3" aria-label="Call TriServe emergency line at 855-435-6631">
                  <Phone className="size-5 text-primary-foreground shrink-0" />
                  <span>855-435-6631</span>
                </a>
              </Button>

              <Button asChild size="lg" className="h-14 px-8 rounded-md bg-accent text-accent-foreground hover:bg-accent/90 font-default text-base font-bold shadow-md transition-[background-color,transform] duration-160 hover:-translate-y-0.5">
                <Link to="/ContactRequestServicePage" className="inline-flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Link>
              </Button>
            </div>

            {/* Technical Verification Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-secondary-foreground/15">
              {featureItems.map((item, i) => <div key={item.title} data-index={i} className="flex items-start gap-3 text-secondary-foreground/90">
                  <CheckCircle2 className="size-5 shrink-0 text-primary-foreground mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold leading-tight text-secondary-foreground">
                      {item.title}
                    </p>
                    <p className="text-xs text-secondary-foreground/70">
                      {item.detail}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Operational Footer Details Bar */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-2 text-xs text-muted-foreground font-mono">
          <div className="flex items-center gap-2">
            <Clock className="size-4 text-primary" />
            <span>24/7 EMERGENCY RESPONSE — IMMEDIATE DISPATCH</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldAlert className="size-4 text-accent" />
            <span>IICRC-CERTIFIED PROTOCOLS & DIRECT ADJUSTER COORDINATION</span>
          </div>
        </div>
      </div>
    </section>;
}